import React, { useEffect, useState } from "react";
import Helmet from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import { PROFILE } from "common/viewContent";
import FormikForm from "components/shared/FormikForm";
import Validation from "./Validation";
import InputFields from "./inputFields";

import { updateAdmin, detailsAdmin } from "store/actions";
import { toast } from "react-toastify";

const Profile = ({ submitting, loader }) => {
  const dispatch = useDispatch();
  const { admin, detailsLoading } = useSelector((state) => state.Auth);
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  useEffect(() => {
    const callBack = (err) => {
      if (err) toast.error(err);
    };
    dispatch(detailsAdmin({ callBack }));
  }, []);

  const initialValues = {
    _id: admin?._id || "",
    name: admin?.name || "",
    email: admin?.email || "",
    countryCode: admin?.countryCode || "+91",
    mobileNumber: admin?.mobileNumber || "",
    profilePic: admin?.profilePic || "",
  };

  const updateProfileSubmit_ = async (values) => {
    try {
      const query = {
        _id: values._id,
        name: values.name,
        email: values.email,
        countryCode: values.countryCode,
        mobileNumber: values.mobileNumber,
        profilePic: values.profilePic,
      };

      const callBack = (err) => {
        if (err) {
          toast.error(err);
        } else {
          toast.success("Profile Updated");
          dispatch(detailsAdmin({ callBack: () => { } })); // refresh after save
        }
      };
      dispatch(updateAdmin({ data: query, callBack }));
    } catch (err) {
      console.error(err);
    }
  };

  const formatDate = (date) =>
    date ? moment(date).format("DD MMM YYYY, hh:mm A") : "N/A";

  return (
    <>
      <Helmet title={PROFILE.CURRENT_MODULE} />
      <section className="section">
        <div className="section-header">
          <h1>{PROFILE.CURRENT_MODULE}</h1>
        </div>

        <div className="section-body">
          <h2 className="section-title">Hi, {admin?.name}!</h2>
          <p className="section-lead m-2">
            Change information about yourself on this page.
          </p>

          {/* Editable fields */}
          <div className="row mt-sm-4">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <FormikForm
                    initialValues={initialValues}
                    validationSchema={Validation}
                    onSubmit={updateProfileSubmit_}
                    loading={loader || detailsLoading}
                    inputFields={InputFields}
                    submitting={submitting}
                    buttonText="Submit"
                    is_block={false}
                    col={6}
                    imageRemoveButton={true}
                    enableReinitialize={true} 
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Read-only account info */}
          <div className="row mt-4">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <h4 className="mb-4">Account Information</h4>
                  <div className="row">
                    <InfoItem label="Role" value={admin?.role} />
                    <InfoItem
                      label="Status"
                      value={
                        <span
                          className={`badge badge-${admin?.status === "active" ? "success" : "danger"
                            }`}
                        >
                          {admin?.status}
                        </span>
                      }
                    />
                    <InfoItem
                      label="Password Changed"
                      value={admin?.isPasswordChange ? "Yes" : "No"}
                    />
                    <InfoItem
                      label="Last Password Change At"
                      value={formatDate(admin?.lastPasswordChangeAt)}
                    />
                    <InfoItem
                      label="Last Login Time"
                      value={formatDate(admin?.lastLoginAt)}
                    />

                    {admin?.role === "ADMIN" && (
                      <InfoItem
                        label="Role Type"
                        value={admin?.roleDetails?.name}
                      />
                    )}

                    <InfoItem
                      label="Created Time"
                      value={formatDate(admin?.createdAt)}
                    />
                    <InfoItem
                      label="Updated Time"
                      value={formatDate(admin?.updatedAt)}
                    />
                    <InfoItem
                      label="Created By"
                      value={
                        typeof admin?.createdBy === "object"
                          ? admin?.createdBy?.name || admin?.createdBy?.email || "N/A"
                          : admin?.createdBy || "N/A"
                      }
                    />

                    <div className="col-md-4 col-sm-6 mb-3">
                      <label className="text-muted mb-1 d-block">
                        Backup Codes
                      </label>
                      {!showBackupCodes ? (
                        <button
                          type="button"
                          className="btn btn-icon btn-sm"
                          title="View Backup Codes"
                          onClick={() => setShowBackupCodes(true)}
                        >
                          <i className="fas fa-eye" />
                        </button>
                      ) : (
                        <div>
                          <div className="d-flex flex-wrap">
                            {admin?.backupCodes?.map((code, idx) => (
                              <span
                                key={idx}
                                className="badge badge-secondary m-1"
                              >
                                {code}
                              </span>
                            ))}
                          </div>
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 mt-1"
                            onClick={() => setShowBackupCodes(false)}
                          >
                            Hide
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const InfoItem = ({ label, value }) => (
  <div className="col-md-4 col-sm-6 mb-3">
    <label className="text-muted mb-1 d-block">{label}</label>
    <div className="font-weight-bold">{value ?? "N/A"}</div>
  </div>
);

export default Profile;